import React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { FiPlus } from 'react-icons/fi';
import { MdClose } from 'react-icons/md';

import styles from './modalMetricCreate.module.scss';

type KeyValueRowsProps = {
  /** react-hook-form field-array path, e.g. `config.headers` or `config.promptParameters`. */
  name: string;
  /** Object property holding the key, e.g. `header` or `key`. */
  keyField: string;
  /** Object property holding the value, e.g. `value`. */
  valueField: string;
  keyLabel: string;
  valueLabel: string;
  addLabel: string;
  testIdPrefix: string;
  disabled?: boolean;
};

/**
 * Editable list of key/value rows backed by a react-hook-form field array.
 * Local to the metric modal; shared by the API Headers and LLM Prompt
 * Parameters sections. Add/remove and edits all mark the form dirty, which the
 * version flow relies on to gate the submit.
 */
export const KeyValueRows: React.FC<KeyValueRowsProps> = ( {
  name,
  keyField,
  valueField,
  keyLabel,
  valueLabel,
  addLabel,
  testIdPrefix,
  disabled
} ) => {
  const { control, register } = useFormContext();
  const { fields, append, remove } = useFieldArray( { control, name } );

  return (
    <div className="layout__col" data-testid={ `${ testIdPrefix }-rows` }>
      {fields.length > 0 && (
        <div className={ styles.kv_header }>
          <span className="auth_flow_label">{keyLabel}</span>
          <span className="auth_flow_label">{valueLabel}</span>
          <span className={ styles.kv_spacer } />
        </div>
      )}
      {fields.map( ( field, index ) => (
        <div className={ styles.kv_row } key={ field.id }>
          <input
            className="auth_flow_input"
            type="text"
            disabled={ disabled }
            data-testid={ `${ testIdPrefix }-key-${ index }` }
            { ...register( `${ name }.${ index }.${ keyField }` ) }
          />
          <input
            className="auth_flow_input"
            type="text"
            disabled={ disabled }
            data-testid={ `${ testIdPrefix }-value-${ index }` }
            { ...register( `${ name }.${ index }.${ valueField }` ) }
          />
          <button
            type="button"
            className={ styles.kv_remove }
            onClick={ () => remove( index ) }
            disabled={ disabled }
            aria-label={ `Remove ${ keyLabel }` }
            data-testid={ `${ testIdPrefix }-remove-${ index }` }
          >
            <MdClose />
          </button>
        </div>
      ) )}
      <button
        type="button"
        className={ styles.kv_add }
        onClick={ () => append( { [ keyField ]: '', [ valueField ]: '' } ) }
        disabled={ disabled }
        data-testid={ `${ testIdPrefix }-add` }
      >
        <FiPlus /> {addLabel}
      </button>
    </div>
  );
};
